import { useCallback, useMemo, useRef } from "react";
import { useWhatsAppCampaignWizard } from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/hooks/UseWhatsAppCampaignWizard";
import { useWhatsAppTemplates } from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/hooks/UseWhatsAppTemplates";
import { NO_LIST_NAMES } from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/WhatsAppCampaignConstants";
import type {
  WhatsAppCampaignStepKey,
  WhatsAppMediaState,
  WhatsAppTemplate,
  WhatsAppVariableDrafts,
} from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/WhatsAppCampaignTypes";
import type { ChatCampaignSetupResult } from "./UseChatCampaignSetup";

export interface ChatWhatsAppStepSnapshot {
  stepIndex: number;
  stepKey: WhatsAppCampaignStepKey;
  template: WhatsAppTemplate | null;
  drafts: WhatsAppVariableDrafts;
  media: WhatsAppMediaState;
}

interface UseChatWhatsAppCampaignWizardParams {
  campaignData: Partial<ChatCampaignSetupResult>;
  onAdvance: (_snapshot: ChatWhatsAppStepSnapshot) => void;
  onBackToSetup: () => void;
}

export const useChatWhatsAppCampaignWizard = ({
  campaignData,
  onAdvance,
  onBackToSetup,
}: UseChatWhatsAppCampaignWizardParams) => {
  const templatesState = useWhatsAppTemplates();
  const wizardBase = useWhatsAppCampaignWizard({
    listNames: campaignData.sendTo ?? NO_LIST_NAMES,
    campaignName: campaignData.campaignName ?? "",
    onTemplatesStale: templatesState.reload,
  });
  const { stepIndex, stepKey, template, drafts, media } = wizardBase;
  const latestStepIndexRef = useRef(stepIndex);
  latestStepIndexRef.current = stepIndex;

  const snapshotBeforeAdvance = useCallback(
    (): ChatWhatsAppStepSnapshot => ({ stepIndex, stepKey, template, drafts, media }),
    [drafts, media, stepIndex, stepKey, template]
  );

  const goNext = useCallback(() => {
    const snapshot = snapshotBeforeAdvance();
    const stepIndexBeforeAdvance = stepIndex;
    wizardBase.goNext();
    if (latestStepIndexRef.current !== stepIndexBeforeAdvance) {
      onAdvance(snapshot);
    }
  }, [onAdvance, snapshotBeforeAdvance, stepIndex, wizardBase]);

  const selectTemplate = useCallback(
    (nextTemplate: WhatsAppTemplate) => {
      const snapshot = snapshotBeforeAdvance();
      const stepIndexBeforeAdvance = stepIndex;
      wizardBase.selectTemplate(nextTemplate);
      if (latestStepIndexRef.current !== stepIndexBeforeAdvance) {
        onAdvance(snapshot);
      }
    },
    [onAdvance, snapshotBeforeAdvance, stepIndex, wizardBase]
  );

  const goBack = useCallback(() => {
    if (stepIndex === 0) {
      onBackToSetup();
      return;
    }
    wizardBase.goBack();
  }, [onBackToSetup, stepIndex, wizardBase]);

  const wizard = useMemo(
    () => ({ ...wizardBase, goNext, selectTemplate, goBack }),
    [goBack, goNext, selectTemplate, wizardBase]
  );

  return { templatesState, wizard };
};
